/**
 * PRDReviewPage - Main container for PRD Review interface.
 *
 * ARCHITECTURE:
 * - React Query (via usePRDReview) is the SINGLE SOURCE OF TRUTH for session data
 * - Zustand (usePRDReviewUIStore) holds only UI state (viewMode, highlights)
 * - No split-brain between frontend and backend state
 *
 * Split-pane layout with:
 * - Left panel (40%): Chat interface for agent collaboration
 * - Right panel (60%): PRD preview/edit with mode toggle
 *
 * Responsive behavior:
 * - Desktop (md+): Side-by-side split pane
 * - Mobile: Stacked vertically with chat on top
 *
 * Keyboard shortcuts:
 * - Escape: Close review and return to project
 */

import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { addToast, Spinner } from '@heroui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { usePRDReview } from '~/hooks/usePRDReview';
import * as prdReviewApi from '~/services/api/prd-review';
import { getProject } from '~/services/api/projects';
import { usePRDReviewUIStore } from '~/stores/prdReviewStore';
import { logger } from '~/utils/logger';

import { ChatPanel } from './ChatPanel';
import { PRDPreviewPanel } from './PRDPreviewPanel';
import { ReviewHeader } from './ReviewHeader';

export function PRDReviewPage(): JSX.Element {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // UI-only state from Zustand
  const viewMode = usePRDReviewUIStore(state => state.viewMode);
  const setViewMode = usePRDReviewUIStore(state => state.setViewMode);
  const lastChangedSection = usePRDReviewUIStore(state => state.lastChangedSection);
  const setLastChangedSection = usePRDReviewUIStore(state => state.setLastChangedSection);

  // Track unsaved changes in PRD content
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // PRD Review hook - SINGLE SOURCE OF TRUTH for session data
  const {
    chatHistory,
    endSession,
    isAgentTyping,
    isSendingMessage,
    isSessionError,
    isSessionLoading,
    isUpdatingContent,
    pendingMessageContent,
    sendMessage,
    sessionError,
    sessionId,
    startSession,
    updateContent,
  } = usePRDReview({
    onPRDUpdate: () => {
      // Refetch PRD content when agent updates it
      void queryClient.invalidateQueries({ queryKey: ['prd-document', projectId] });
    },
    projectId: projectId ?? '',
  });

  // Start session on mount (explicit, not antipattern useEffect in hook)
  useEffect(() => {
    if (projectId) {
      startSession();
    }
  }, [projectId, startSession]);

  // Fetch PRD content
  const {
    data: prdDocument,
    error: prdError,
    isError: isPRDError,
    isLoading: isLoadingPRD,
  } = useQuery({
    enabled: !!projectId && !!sessionId,
    queryFn: () => prdReviewApi.getPRDContent(projectId ?? ''),
    queryKey: ['prd-document', projectId],
    staleTime: 30000, // 30 seconds
  });

  // Fetch project for title
  const { data: project } = useQuery({
    enabled: !!projectId,
    queryFn: () => getProject(projectId ?? ''),
    queryKey: ['project', projectId],
    staleTime: 60000, // 1 minute
  });

  // Handle session errors
  useEffect(() => {
    if (isSessionError && sessionError) {
      logger.error(
        { component: 'PRDReviewPage' },
        'Failed to start review session',
        sessionError instanceof Error ? sessionError : new Error(String(sessionError)),
      );
      addToast({
        color: 'danger',
        description:
          sessionError instanceof Error ? sessionError.message : 'Failed to start review session',
      });
    }
  }, [isSessionError, sessionError]);

  // Handle PRD fetch errors
  useEffect(() => {
    if (isPRDError && prdError) {
      logger.error(
        { component: 'PRDReviewPage' },
        'Failed to load PRD content',
        prdError instanceof Error ? prdError : new Error(String(prdError)),
      );
      addToast({
        color: 'danger',
        description: 'Failed to load PRD content. The PRD may not exist.',
      });
    }
  }, [isPRDError, prdError]);

  const handleClose = useCallback((): void => {
    endSession();
    void navigate(`/projects/${projectId}`);
  }, [endSession, navigate, projectId]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      // Escape to close and return to project
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleClose]);

  const handleSendMessage = useCallback(
    (content: string): void => {
      // Clear any existing highlight when sending a new message
      setLastChangedSection(null);
      sendMessage(content);
    },
    [sendMessage, setLastChangedSection],
  );

  const handleUpdateContent = useCallback(
    (content: string): void => {
      updateContent(content);
    },
    [updateContent],
  );

  const handleClearChangedSection = useCallback((): void => {
    setLastChangedSection(null);
  }, [setLastChangedSection]);

  // Compute disabled state for chat input
  const isChatDisabled = isSendingMessage || isAgentTyping;

  // Loading state
  if (isSessionLoading || (sessionId && isLoadingPRD)) {
    return (
      <div className="flex items-center justify-center h-full bg-surface">
        <div className="text-center">
          <Spinner size="lg" color="primary" />
          <p className="mt-4 text-foreground/70">
            {isSessionLoading ? 'Starting review session...' : 'Loading PRD content...'}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (isSessionError || isPRDError) {
    const isPRDMissing = isPRDError;
    const canRetry = !isPRDMissing; // Only allow retry for session errors, not missing PRD

    return (
      <div className="flex items-center justify-center h-full bg-surface">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-semibold text-foreground mb-2">Unable to Start Review</h2>
          <p className="text-foreground/70 mb-4">
            {isPRDMissing
              ? 'No PRD document found for this project. Please generate a PRD first.'
              : 'Failed to start the review session. Please try again.'}
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={(): void => {
                void navigate(`/projects/${projectId}`);
              }}
              className="px-4 py-2 text-sm font-medium text-foreground/70 border border-border rounded-lg hover:bg-surface-elevated transition-colors"
            >
              Back to Project
            </button>
            {canRetry && (
              <button
                type="button"
                onClick={(): void => {
                  void queryClient.invalidateQueries({
                    queryKey: ['prd-review-session', projectId],
                  });
                }}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-surface">
      {/* Header - Fixed at top, never scrolls */}
      <ReviewHeader
        isAgentTyping={isAgentTyping}
        isSaving={isUpdatingContent}
        hasUnsavedChanges={hasUnsavedChanges}
        questionCount={prdDocument?.questionCount}
        answeredQuestionCount={prdDocument?.answeredQuestionCount}
        onClose={handleClose}
      />

      {/* Split pane content - responsive layout */}
      {/* Mobile: stacked vertically, Desktop: side-by-side */}
      {/* flex-1 takes remaining height, overflow-hidden prevents scroll on parent */}
      <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
        {/* Chat Panel - full width on mobile, 40% on desktop */}
        {/* flex-shrink-0 prevents panel from collapsing when PRD content changes */}
        <div className="h-1/2 md:h-auto md:w-2/5 md:flex-shrink-0 border-b md:border-b-0 md:border-r border-border flex flex-col min-w-0">
          <ChatPanel
            chatHistory={chatHistory}
            isAgentTyping={isAgentTyping}
            isDisabled={isChatDisabled}
            pendingMessageContent={pendingMessageContent}
            onSendMessage={handleSendMessage}
          />
        </div>

        {/* PRD Preview Panel - full width on mobile, 60% on desktop */}
        {/* flex-shrink-0 ensures consistent width regardless of view mode */}
        <div className="h-1/2 md:h-auto md:w-3/5 md:flex-shrink-0 flex flex-col min-w-0">
          <PRDPreviewPanel
            content={prdDocument?.content ?? ''}
            projectTitle={project?.title}
            viewMode={viewMode}
            isEditLocked={isAgentTyping}
            lastChangedSection={lastChangedSection}
            onViewModeChange={setViewMode}
            onContentChange={handleUpdateContent}
            onUnsavedChangesChange={setHasUnsavedChanges}
            onClearChangedSection={handleClearChangedSection}
          />
        </div>
      </div>
    </div>
  );
}
